import { useContext, useMemo, useEffect } from 'react';
import { localStorage, FeaturesAndAmenitiesList, ViewerContext } from 'dash-ui';
import {
  PropertyFragment,
  useGetAmenitiesQuery,
  useGetPropertyAmenitiesInPublicBrokerPortfolioQuery,
} from 'gql/generated.graphql';
import { PropertyPreviewParamsContext } from 'containers/properties/PropertyPreview/context/PropertyPreviewParamsContext';
import { GTMEventsEnum } from 'common/google-tag-manager/constants';
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from 'common/google-tag-manager/helpers';
import {
  buildRangeString,
  buildBuiltRange,
  buildAddress,
} from 'common/helpers';

function AmenitiesSection({
  property,
  isViewAsClient,
  portfolioId,
}: {
  property: PropertyFragment;
  isViewAsClient?: boolean;
  portfolioId?: string;
}) {
  const { account } = useContext(ViewerContext);
  const { params } = useContext(PropertyPreviewParamsContext);
  const { brokerId } = params || {};
  const portfolioVariables = {
    propertyId: property._id,
    brokerId,
    portfolioFolderId: portfolioId || undefined,
    password: localStorage.getPortfolioPassword(),
  };

  const { data: result, loading } = useGetAmenitiesQuery({
    variables: {
      filter: { propertyId: property?._id },
      pagination: { allowLimit: false },
    },
  });

  const { data: publicResult, loading: publicLoading } =
    useGetPropertyAmenitiesInPublicBrokerPortfolioQuery({
      skip: !isViewAsClient || !brokerId,
      variables: {
        ...portfolioVariables,
        filter: { propertyId: property?._id },
      },
    });

  const amenities = useMemo(
    () =>
      result?.getAmenities.collection ||
      publicResult?.getPropertyAmenitiesInPublicBrokerPortfolio.collection ||
      [],
    [result, publicResult]
  );

  useEffect(() => {
    if (!amenities.length || !property?._id || !account) return;
    createGTMEvent({
      dataLayer: {
        event: GTMEventsEnum.PROPERTY_DETAIL_VIEWED,
        user_id: getGoogleTagManagerUserId(account.email),
        property_name: property.name,
        price_range: buildRangeString(property.priceRange),
        property_type: property.buildingTypes,
        number_of_bedrooms: buildRangeString(
          property.averageBedroomsNumberValue
        ),
        number_of_bathrooms: buildRangeString(
          property.averageBathroomsNumberValue
        ),
        number_of_units: property.availableUnitsNumber,
        appeared_date: property.createdAt.split('T')[0],
        features_and_amenities: amenities
          .map(({ name }) => name.trim())
          .join(', '),
        completion_year: property.yearBuilt,
        project_status: property.projectStatus,
        year_built: buildBuiltRange({
          startDate: property.constructionStartDate,
          endDate: property.completionDate,
          yearOnly: true,
        }),
        scheduled_construction: buildBuiltRange({
          startDate: property.constructionStartDate,
          endDate: property.completionDate,
        }),
        marketArea: property.marketArea,
        address: buildAddress(property.location.address),
      },
    });
  }, [property, amenities, account]);

  return (
    <FeaturesAndAmenitiesList
      amenities={amenities}
      loading={loading || publicLoading}
    />
  );
}

export default AmenitiesSection;
